import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

const MassTransferContainer = styled.div`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  margin-top: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const Title = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.3s ease;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  min-height: 150px;
  resize: vertical;
  font-family: 'Courier New', monospace;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const KeysContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DistributionModeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  padding: 12px;
  background: var(--bg-primary);
  border-radius: 8px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DescriptionWrapper = styled.div`
  padding-left: 30px; /* checkbox width (18px) + gap (12px) */
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--accent-primary);
`;

const CheckboxLabel = styled.label`
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HelperText = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 4px;
`;

const TransferButton = styled.button`
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-hover) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LogsContainer = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--text-primary);
`;

const LogsTitle = styled.h3`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const LogEntry = styled.div<{ $level?: 'info' | 'success' | 'error' | 'warning' }>`
  margin-bottom: 4px;
  padding: 4px 0;
  color: ${props => {
    switch (props.$level) {
      case 'success':
        return '#22c55e';
      case 'error':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      default:
        return 'var(--text-primary)';
    }
  }};
`;

const EmptyLogs = styled.div`
  color: var(--text-muted);
  font-style: italic;
`;

interface MassTransferSectionProps {
  rpcUrl?: string;
}

interface LogMessage {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
}

const MassTransferSection: React.FC<MassTransferSectionProps> = ({ rpcUrl }) => {
  const [tokenMint, setTokenMint] = useState('');
  const [fromPrivateKeys, setFromPrivateKeys] = useState('');
  const [toPrivateKeys, setToPrivateKeys] = useState('');
  const [evenDistribution, setEvenDistribution] = useState(false);
  const [leaveTokens, setLeaveTokens] = useState(false);
  const [leaveTokensMin, setLeaveTokensMin] = useState<string>('');
  const [leaveTokensMax, setLeaveTokensMax] = useState<string>('');
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [isTransferring, setIsTransferring] = useState(false);

  const addLog = (level: LogMessage['level'], message: string) => {
    const newLog: LogMessage = {
      timestamp: new Date().toISOString(),
      level,
      message,
    };
    setLogs(prev => [...prev, newLog]);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const handleTransfer = async () => {
    // Валидация
    if (!fromPrivateKeys.trim()) {
      toast.error('Please enter sender private keys');
      addLog('error', 'Error: Sender private keys are required');
      return;
    }

    if (!toPrivateKeys.trim()) {
      toast.error('Please enter receiver private keys');
      addLog('error', 'Error: Receiver private keys are required');
      return;
    }

    // Парсим приватные ключи
    const fromKeys = fromPrivateKeys
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    const toKeys = toPrivateKeys
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (fromKeys.length === 0) {
      toast.error('No valid sender private keys found');
      addLog('error', 'Error: No valid sender private keys found');
      return;
    }

    if (toKeys.length === 0) {
      toast.error('No valid receiver private keys found');
      addLog('error', 'Error: No valid receiver private keys found');
      return;
    }

    // Для прямого трансфера количество должно совпадать
    if (!evenDistribution && fromKeys.length !== toKeys.length) {
      toast.error(`For direct transfer, the number of senders (${fromKeys.length}) must match the number of receivers (${toKeys.length})`);
      addLog('error', `Error: Count mismatch - ${fromKeys.length} senders vs ${toKeys.length} receivers`);
      return;
    }

    setIsTransferring(true);
    clearLogs();
    addLog('info', `Starting ${evenDistribution ? 'even distribution' : 'direct transfer'}...`);
    addLog('info', `Senders: ${fromKeys.length}, Receivers: ${toKeys.length}`);
    if (tokenMint.trim()) {
      addLog('info', `Token: ${tokenMint.trim()}`);
    } else {
      addLog('info', 'Token: Not specified (ETH only)');
    }

    // Валидация leaveTokens полей
    if (leaveTokens) {
      if (!leaveTokensMin.trim() && !leaveTokensMax.trim()) {
        toast.error('Please enter at least one value for leave tokens (min or max)');
        addLog('error', 'Error: Leave tokens min or max is required when leave tokens is enabled');
        return;
      }
      
      const min = leaveTokensMin.trim() ? parseFloat(leaveTokensMin.trim()) : null;
      const max = leaveTokensMax.trim() ? parseFloat(leaveTokensMax.trim()) : null;
      
      if (min !== null && isNaN(min)) {
        toast.error('Leave tokens min must be a valid number');
        addLog('error', 'Error: Leave tokens min must be a valid number');
        return;
      }
      
      if (max !== null && isNaN(max)) {
        toast.error('Leave tokens max must be a valid number');
        addLog('error', 'Error: Leave tokens max must be a valid number');
        return;
      }
      
      if (min !== null && max !== null && min > max) {
        toast.error('Leave tokens min cannot be greater than max');
        addLog('error', 'Error: Leave tokens min cannot be greater than max');
        return;
      }
    }

    try {
      const response = await apiService.massTransfer({
        tokenMint: tokenMint.trim() || undefined,
        fromAddresses: fromKeys,
        toAddresses: toKeys,
        evenDistribution,
        rpc_url: rpcUrl,
        leaveTokens,
        leaveTokensMin: leaveTokens ? (leaveTokensMin.trim() ? parseFloat(leaveTokensMin.trim()) : null) : null,
        leaveTokensMax: leaveTokens ? (leaveTokensMax.trim() ? parseFloat(leaveTokensMax.trim()) : null) : null,
      });

      addLog('success', response.message || 'Transfer completed successfully');
      addLog('info', `Mode: ${response.mode}`);
      addLog('info', `Network: ${response.network}`);
      
      if (response.from_count) {
        addLog('info', `From count: ${response.from_count}`);
      }
      if (response.to_count) {
        addLog('info', `To count: ${response.to_count}`);
      }
      if (response.pairs_count) {
        addLog('info', `Pairs: ${response.pairs_count}`);
      }
      
      toast.success(response.message || 'Transfer completed successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      addLog('error', `Transfer failed: ${errorMessage}`);
      toast.error(`Transfer failed: ${errorMessage}`);
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <MassTransferContainer>
      <Title>
        🌿 Mass Transfer
      </Title>

      <InputGroup>
        <Label>Token Mint Address</Label>
        <Input
          type="text"
          placeholder="0x... (leave empty for ETH transfer only)"
          value={tokenMint}
          onChange={(e) => setTokenMint(e.target.value)}
        />
        <HelperText>
          The token address to transfer (optional, leave empty for ETH only)
        </HelperText>
      </InputGroup>

      <KeysContainer>
        <InputGroup>
          <Label>From Private Keys</Label>
          <TextArea
            placeholder="Enter private keys (one per line)"
            value={fromPrivateKeys}
            onChange={(e) => setFromPrivateKeys(e.target.value)}
          />
          <HelperText>
            Sender private keys (one per line)
          </HelperText>
        </InputGroup>

        <InputGroup>
          <Label>To Private Keys</Label>
          <TextArea
            placeholder="Enter private keys (one per line)"
            value={toPrivateKeys}
            onChange={(e) => setToPrivateKeys(e.target.value)}
          />
          <HelperText>
            Receiver private keys (one per line)
          </HelperText>
        </InputGroup>
      </KeysContainer>

      <DistributionModeContainer>
        <CheckboxRow>
          <Checkbox
            type="checkbox"
            id="evenDistribution"
            checked={evenDistribution}
            onChange={(e) => setEvenDistribution(e.target.checked)}
          />
          <CheckboxLabel htmlFor="evenDistribution">
            Even distribution
          </CheckboxLabel>
        </CheckboxRow>
        <DescriptionWrapper>
          <HelperText style={{ margin: 0 }}>
            {evenDistribution 
              ? 'Funds will be evenly distributed among all receivers.'
              : 'Each sender transfers funds to the corresponding receiver: first sender → first receiver, second sender → second receiver, etc. The number of senders must match the number of receivers.'}
          </HelperText>
        </DescriptionWrapper>
      </DistributionModeContainer>

      <DistributionModeContainer>
        <CheckboxRow>
          <Checkbox
            type="checkbox"
            id="leaveTokens"
            checked={leaveTokens}
            onChange={(e) => setLeaveTokens(e.target.checked)}
          />
          <CheckboxLabel htmlFor="leaveTokens">
            Leave tokens on wallets
          </CheckboxLabel>
        </CheckboxRow>
        <DescriptionWrapper>
          <HelperText style={{ margin: 0, marginBottom: '12px' }}>
            Enable to keep a random amount of tokens within the specified range on each wallet after transfer.
          </HelperText>
          {leaveTokens && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <InputGroup style={{ margin: 0, flex: '1', minWidth: '150px' }}>
                <Label style={{ fontSize: '0.75rem' }}>Min tokens to leave</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={leaveTokensMin}
                  onChange={(e) => setLeaveTokensMin(e.target.value)}
                  step="any"
                  min="0"
                />
              </InputGroup>
              <InputGroup style={{ margin: 0, flex: '1', minWidth: '150px' }}>
                <Label style={{ fontSize: '0.75rem' }}>Max tokens to leave</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={leaveTokensMax}
                  onChange={(e) => setLeaveTokensMax(e.target.value)}
                  step="any"
                  min="0"
                />
              </InputGroup>
            </div>
          )}
        </DescriptionWrapper>
      </DistributionModeContainer>

      <TransferButton
        onClick={handleTransfer}
        disabled={isTransferring}
      >
        {isTransferring ? (
          <>⏳ Transferring...</>
        ) : (
          <>🚀 Start transfer</>
        )}
      </TransferButton>

      <LogsContainer>
        <LogsTitle>Operation logs</LogsTitle>
        {logs.length === 0 ? (
          <EmptyLogs>No logs yet</EmptyLogs>
        ) : (
          logs.map((log, index) => (
            <LogEntry key={index} $level={log.level}>
              [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
            </LogEntry>
          ))
        )}
      </LogsContainer>
    </MassTransferContainer>
  );
};

export default MassTransferSection;

